import type { OrganisationOpportunity } from '../hooks/use-organisation-opportunities'

type PublishCheckInput = Pick<
  OrganisationOpportunity,
  | 'employment_type'
  | 'work_mode'
  | 'city'
  | 'country_code'
  | 'salary_visibility'
  | 'salary_min'
  | 'salary_max'
  | 'salary_currency'
  | 'salary_period'
>

export type PublishBlockerCode =
  | 'employment_type'
  | 'work_mode'
  | 'city'
  | 'country_code'
  | 'salary'

export type PublishBlocker = {
  code: PublishBlockerCode
  /** Why publish is blocked (sentence). */
  message: string
  /** Short empty-slot control — “Add city”, shown in the job card. */
  actionLabel: string
}

function blocker(
  code: PublishBlockerCode,
  message: string,
  actionLabel: string,
): PublishBlocker {
  return { code, message, actionLabel }
}

/** Mirrors api/apps/opportunities/meta.py validate_managed_opportunity_for_publish. */
export function listOpportunityPublishBlockers(opportunity: PublishCheckInput): PublishBlocker[] {
  const blockers: PublishBlocker[] = []

  if (!(opportunity.employment_type || '').trim()) {
    blockers.push(
      blocker('employment_type', 'Employment type is missing from job details.', 'Add employment type'),
    )
  }

  const workMode = (opportunity.work_mode || '').trim()
  if (!workMode) {
    blockers.push(
      blocker('work_mode', 'Work mode is missing from job details.', 'Add work mode'),
    )
  } else if (workMode === 'remote') {
    if (!(opportunity.country_code || '').trim()) {
      blockers.push(
        blocker('country_code', 'Country is missing from job details (required for remote).', 'Add country'),
      )
    }
  } else if (workMode === 'onsite' || workMode === 'hybrid') {
    if (!(opportunity.city || '').trim()) {
      blockers.push(
        blocker(
          'city',
          'City is missing from job details (required for on-site or hybrid).',
          'Add city',
        ),
      )
    }
    if (!(opportunity.country_code || '').trim()) {
      blockers.push(
        blocker(
          'country_code',
          'Country is missing from job details (required for on-site or hybrid).',
          'Add country',
        ),
      )
    }
  }

  const visibility = (opportunity.salary_visibility || 'not_disclosed').trim()
  if (visibility === 'shown') {
    if (opportunity.salary_min == null || opportunity.salary_max == null) {
      blockers.push(
        blocker(
          'salary',
          'Salary range is missing from job details, or choose not to disclose.',
          'Add salary',
        ),
      )
    } else if (opportunity.salary_min > opportunity.salary_max) {
      blockers.push(
        blocker('salary', 'Salary maximum must be greater than or equal to minimum.', 'Fix salary'),
      )
    } else {
      const currency = (opportunity.salary_currency || '').trim().toUpperCase()
      if (currency.length !== 3) {
        blockers.push(
          blocker('salary', 'Salary currency is missing from job details.', 'Add salary currency'),
        )
      }
      const period = (opportunity.salary_period || '').trim()
      if (!['year', 'month', 'day', 'hour'].includes(period)) {
        blockers.push(
          blocker('salary', 'Salary period is missing from job details.', 'Add salary period'),
        )
      }
    }
  }

  return blockers
}

export function getOpportunityPublishBlockers(opportunity: PublishCheckInput): string[] {
  return listOpportunityPublishBlockers(opportunity).map((blocker) => blocker.message)
}

export function canPublishOpportunity(opportunity: PublishCheckInput): boolean {
  return listOpportunityPublishBlockers(opportunity).length === 0
}

function uniquePublishBlockers(blockers: PublishBlocker[]): PublishBlocker[] {
  const seen = new Set<string>()
  const unique: PublishBlocker[] = []
  for (const blocker of blockers) {
    const key = `${blocker.code}:${blocker.actionLabel}`
    if (seen.has(key)) continue
    seen.add(key)
    unique.push(blocker)
  }
  return unique
}

/** “city”, “city and country”, “employment type, work mode, and city” */
export function formatPublishBlockerActionLabelsPhrase(blockers: PublishBlocker[]): string {
  const labels = uniquePublishBlockers(blockers).map((blocker) =>
    blocker.actionLabel
      .replace(/^Add\s+/i, '')
      .replace(/^Fix\s+/i, '')
      .toLowerCase(),
  )
  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}
